import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface CurrentCollectionState {
    // The currently selected collection ID (most recently interacted)
    currentCollectionId: string | null

    // Timestamp of last interaction
    lastInteraction: number | null

    // Actions
    setCurrentCollection: (collectionId: string) => void
    clearCurrentCollection: () => void

    // Getters
    getCurrentCollection: () => string | null
    hasCurrentCollection: () => boolean
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

export const useCurrentCollectionStore = create<CurrentCollectionState>()(
    persist(
        (set, get) => ({
            currentCollectionId: null,
            lastInteraction: null,

            setCurrentCollection: (collectionId: string) => {
                const now = Date.now()
                set({
                    currentCollectionId: collectionId,
                    lastInteraction: now,
                })
            },

            clearCurrentCollection: () => {
                set({
                    currentCollectionId: null,
                    lastInteraction: null,
                })
            },

            getCurrentCollection: () => {
                const state = get()

                // Check if the current selection is stale (older than 30 days)
                if (
                    state.lastInteraction &&
                    Date.now() - state.lastInteraction > THIRTY_DAYS_MS
                ) {
                    // Auto-clear stale selection
                    get().clearCurrentCollection()
                    return null
                }

                return state.currentCollectionId
            },

            hasCurrentCollection: () => {
                return get().getCurrentCollection() !== null
            },
        }),
        {
            name: 'current-collection-storage',
            version: 1,
            // Auto-cleanup stale data on load
            partialize: (state) => {
                const now = Date.now()
                const isStale =
                    state.lastInteraction &&
                    now - state.lastInteraction > THIRTY_DAYS_MS

                if (isStale) {
                    return {
                        currentCollectionId: null,
                        lastInteraction: null,
                    }
                }

                return {
                    currentCollectionId: state.currentCollectionId,
                    lastInteraction: state.lastInteraction,
                }
            },
        }
    )
)
